// @flow
import React, {useState} from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {Box, IconButton, makeStyles} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';

import {DeleteOutline, Edit, DragIndicator, Add} from '@material-ui/icons';

import type {Subcategory} from '../../../../entities';
import {SortableItem, SortableKnob} from 'react-easy-sort';
import {useToggle} from '../../../../utils/react';
import SubcategoryItemList from './SubcategoryItemList';
import SubcategoryItemModal from './SubcategoryItemModal';
import {useSubCategoriesItem} from '../../../sub-categories/utils';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';
import {SUBCATEGORY_ITEM_ENABLED} from './SubcategoryList';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import {useToastActions} from '../../../toast-alert/utils';

type Props = {
  subcategory: Subcategory,
  onDeleteSubcategory: (subcategoryId: string) => void,
  onEditSubcategory: (subcategory: Subcategory) => void,
};

const useStyles = makeStyles(theme => ({
  subcategoryItem: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    background: colors.grey200,
    padding: '.25rem',
    paddingLeft: '0',
    borderRadius: '.5rem',
    position: 'relative',
    flexDirection: 'column',
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: `0px 8px 24px rgba(66, 64, 64, 0.12), 0 0 0 1px ${colors.primary}`,
      backgroundColor: colors.lightPurple,
      zIndex: 1000,
    },
  },
  itemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  horizontalDivider: {
    width: 2,
    height: '35px',
    backgroundColor: '#D4D4DD',
    margin: 'auto 12px',
  },
  itemName: {
    color: colors.grey800,
    fontSize: '.75rem',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trashIcon: {
    color: '#E54D7B',
    float: 'right',
    cursor: 'pointer',
  },
  editIcon: {
    color: '#717190',
    float: 'right',
    cursor: 'pointer',
  },
  iconButton: {
    padding: '.25rem',
  },
  dragIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.grey600,
    alignSelf: 'normal',

    '&:hover': {
      cursor: 'grab',
      color: colors.primary,
    },
    '&:active': {
      cursor: 'grabbing',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '.25rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '.5rem',
    },
  },
}));

function SubcategoryRowComponent(props: Props) {
  const {subcategory, onDeleteSubcategory, onEditSubcategory} = props;
  const classes = useStyles();

  const [subItemsOpen, toggleSubItemsOpen] = useToggle(false);
  const [modalSubcategoryOpen, toggleModalSubcategoryOpen] = useToggle(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const {createSubCategoryItem} = useSubCategoriesItem();
  const {showToast} = useToastActions();

  const hasSubItems =
    subcategory.subCategoriesItems && subcategory.subCategoriesItems.length > 0;

  const handleDeleteClick = () => {
    if (hasSubItems) {
      showToast({
        type: 'warning',
        title: 'No se puede eliminar',
        message:
          'No puedes eliminar esta subcategoría porque tiene items asociados. Primero elimina los items.',
      });
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    onDeleteSubcategory(subcategory.id);
    setConfirmDeleteOpen(false);
  };

  return (
    <SortableItem key={subcategory.id}>
      <div className={classes.subcategoryItem}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexGrow={1}
        >
          <SortableKnob>
            <span className={classes.dragIndicator}>
              <DragIndicator fontSize="small" />
            </span>
          </SortableKnob>
          <div className={classes.itemContent}>
            <span className={classes.itemName}>{subcategory.name}</span>
            <div className={classes.rightSection}>
              <TiendadaTooltip title="Editar subcategoría" placement="top">
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    onEditSubcategory(subcategory);
                  }}
                  size="small"
                  className={classes.iconButton}
                >
                  <Edit fontSize="small" className={classes.editIcon} />
                </IconButton>
              </TiendadaTooltip>

              <TiendadaTooltip
                title={
                  hasSubItems
                    ? 'Elimina primero los items'
                    : 'Eliminar subcategoría'
                }
                placement="top"
              >
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteClick();
                  }}
                  size="small"
                  className={classes.iconButton}
                >
                  <DeleteOutline
                    fontSize="small"
                    className={classes.trashIcon}
                  />
                </IconButton>
              </TiendadaTooltip>

              {SUBCATEGORY_ITEM_ENABLED && (
                <>
                  {hasSubItems ? (
                    <TiendadaTooltip title="Mostrar items" placement="top">
                      <IconButton
                        onClick={toggleSubItemsOpen}
                        className={classes.iconButton}
                        size="medium"
                      >
                        {subItemsOpen ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </TiendadaTooltip>
                  ) : (
                    <TiendadaTooltip title="Agregar item" placement="top">
                      <IconButton
                        onClick={toggleModalSubcategoryOpen}
                        className={classes.iconButton}
                        size="medium"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </TiendadaTooltip>
                  )}
                </>
              )}
            </div>
          </div>
        </Box>
        {subItemsOpen && (
          <SubcategoryItemList
            subcategory={subcategory}
            subcategoriesItem={subcategory.subCategoriesItems ?? []}
          />
        )}
        <SubcategoryItemModal
          onClose={toggleModalSubcategoryOpen}
          open={modalSubcategoryOpen}
          onSubmit={name => {
            createSubCategoryItem({
              subCategoryId: subcategory.id,
              name,
            }).then(() => {
              toggleModalSubcategoryOpen();
            });
          }}
          subcategory={subcategory.name}
        />
        <ConfirmModal
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={handleConfirmDelete}
          title="¿Eliminar subcategoría?"
          message={`¿Estás seguro de que deseas eliminar la subcategoría "${subcategory.name}"? Esta acción no se puede deshacer.`}
          confirmButtonLabel="Eliminar"
          cancelButtonLabel="Cancelar"
        />
      </div>
    </SortableItem>
  );
}

const SubcategoryRow = React.memo(SubcategoryRowComponent);

export default SubcategoryRow;
